import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Download, X } from "lucide-react";

const INSTALL_PASSWORD = "Bugzy";

/**
 * Shows a discreet "Install app" button when the browser supports PWA installation.
 * Requires a shared password ("Bugzy") before triggering the install prompt or
 * showing the iOS "Add to Home Screen" hint, so it can't be installed on personal
 * devices without authorisation.
 */
export default function InstallAppButton() {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [pwd, setPwd] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setInstalled(true); setDeferred(null); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    const mq = window.matchMedia("(display-mode: standalone)");
    if (mq.matches || window.navigator.standalone) setInstalled(true);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed) return null;

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  if (!deferred && !isIOS) return null;

  const openPwd = () => { setPwd(""); setErr(""); setShowPwd(true); };
  const close = () => { setShowPwd(false); setPwd(""); setErr(""); };

  const submit = async () => {
    if (pwd !== INSTALL_PASSWORD) {
      setErr("Incorrect password.");
      return;
    }
    close();
    if (deferred) {
      try { await deferred.prompt(); await deferred.userChoice; } catch (_) { /* ignore */ }
      setDeferred(null);
    } else if (isIOS) {
      alert("To install: tap the Share icon in Safari, then choose 'Add to Home Screen'.");
    }
  };

  return (
    <>
      <Button
        data-testid="install-app-btn"
        size="sm"
        variant="outline"
        onClick={openPwd}
        className="rounded-sm border-orange-300 bg-orange-50 text-orange-800 hover:bg-orange-100 h-9 px-2 sm:px-3"
      >
        <Download className="h-3.5 w-3.5 sm:mr-1.5" />
        <span className="hidden sm:inline">Install app</span>
      </Button>

      {showPwd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 backdrop-blur-sm" onClick={close} data-testid="install-pwd-modal">
          <div className="bg-white rounded-sm border border-slate-200 shadow-xl w-full max-w-sm mx-4 p-6 relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={close} className="absolute top-3 right-3 p-1 rounded-sm hover:bg-slate-100 text-slate-500" data-testid="install-pwd-close">
              <X className="w-4 h-4" />
            </button>
            <div className="text-[11px] uppercase tracking-[0.22em] text-orange-600 font-semibold">Authorised install only</div>
            <div className="mt-2 font-display text-xl font-semibold text-slate-900">Enter install password</div>
            <p className="mt-2 text-sm text-slate-600">This device can only install the D4PS app with the correct password.</p>
            <Input
              data-testid="install-pwd-input"
              type="password"
              autoFocus
              value={pwd}
              onChange={(e) => { setPwd(e.target.value); setErr(""); }}
              onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
              className="mt-4 rounded-sm h-11 border-slate-300 focus-visible:ring-slate-900"
              placeholder="Password"
            />
            {err && <div className="mt-2 text-sm text-red-700 bg-red-50 border border-red-200 px-2.5 py-1.5 rounded-sm" data-testid="install-pwd-error">{err}</div>}
            <div className="mt-4 flex items-center justify-end gap-2">
              <Button variant="ghost" onClick={close} className="rounded-sm">Cancel</Button>
              <Button onClick={submit} data-testid="install-pwd-submit" className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white">
                <Download className="h-4 w-4 mr-1.5" /> Continue
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
